import {Injectable} from '@angular/core';
import {FormControl, ValidatorFn, Validators} from '@angular/forms';
import {FieldMinimal} from '@docma-mm-core/field/field';
import {FieldPermission} from '@docma-mm-core/field/field-permission.enum';
import {
  classificationFieldProperties,
  costFieldProperties,
  descriptionFieldProperties,
  executionFieldProperties,
  projectFieldProperties,
  responsibleFieldProperties
} from '@docma-mm-core/field/field-property-groups';
import {FieldPropertyEnum} from '@docma-mm-core/field/field-property.enum';
import {ProjectActionPermissionEnum} from '@docma-mm-core/permission/project-action-permission.enum';
import {
  FaultFieldTypeEnum
} from 'projects/mydocma/mm/src/lib/mm-faults/mm-fault/mm-fault-detail-form/fault-field-type.enum';
import {forkJoin} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {MmProjectPermissionsService} from '../../mm-project/mm-project-permissions-service';
import {FaultField} from './mm-fault-detail-form/fault-field';
import {FaultFieldGroups} from './mm-fault-detail-form/fault-field-groups';
import {CreateSyncStateFieldValidator, ValidateFieldError,} from "./create-sync-state-field-validator";
import {ViewFault} from "@docma-mm-core/fault/fault";


@Injectable()
export class MmFaultFormService {

  propertyToControlTypeMap = new Map<string, FaultFieldTypeEnum >([
    [FieldPropertyEnum.Number1, FaultFieldTypeEnum.Label],
    [FieldPropertyEnum.Number2, FaultFieldTypeEnum.Text],
    [FieldPropertyEnum.Desc1_1, FaultFieldTypeEnum.TextArea],
    [FieldPropertyEnum.Desc1_2, FaultFieldTypeEnum.TextArea],
    [FieldPropertyEnum.Desc1_3, FaultFieldTypeEnum.TextArea],
    [FieldPropertyEnum.Desc2_1, FaultFieldTypeEnum.TextArea],
    [FieldPropertyEnum.Desc2_2, FaultFieldTypeEnum.TextArea],
    [FieldPropertyEnum.Desc2_3, FaultFieldTypeEnum.TextArea],
    [FieldPropertyEnum.Text1, FaultFieldTypeEnum.TextArea],
    [FieldPropertyEnum.Text2, FaultFieldTypeEnum.TextArea],
    [FieldPropertyEnum.Text3, FaultFieldTypeEnum.TextArea],
    [FieldPropertyEnum.Date1, FaultFieldTypeEnum.Date],
    [FieldPropertyEnum.Date2, FaultFieldTypeEnum.Date],
    [FieldPropertyEnum.Date3, FaultFieldTypeEnum.Date],
    [FieldPropertyEnum.Structure, FaultFieldTypeEnum.Structure],
    [FieldPropertyEnum.Company, FaultFieldTypeEnum.Selection],
    [FieldPropertyEnum.Craft, FaultFieldTypeEnum.Selection],
    [FieldPropertyEnum.Order, FaultFieldTypeEnum.Selection],
    [FieldPropertyEnum.ContactCO, FaultFieldTypeEnum.Selection],
    [FieldPropertyEnum.ContactCT, FaultFieldTypeEnum.Selection],
    [FieldPropertyEnum.ContactSC, FaultFieldTypeEnum.Selection],
    [FieldPropertyEnum.Attribute, FaultFieldTypeEnum.Selection],
    [FieldPropertyEnum.AttributeGroup_Image, FaultFieldTypeEnum.Selection],
    [FieldPropertyEnum.Class1, FaultFieldTypeEnum.Selection],
    [FieldPropertyEnum.Class2, FaultFieldTypeEnum.Selection],
    [FieldPropertyEnum.Class3, FaultFieldTypeEnum.Selection],
    [FieldPropertyEnum.Class4, FaultFieldTypeEnum.Selection],
    [FieldPropertyEnum.UserDefined1, FaultFieldTypeEnum.Selection],
    [FieldPropertyEnum.UserDefined2, FaultFieldTypeEnum.Selection],
    [FieldPropertyEnum.CostsCompany, FaultFieldTypeEnum.Selection],
    [FieldPropertyEnum.CostsOrder, FaultFieldTypeEnum.Selection],
    [FieldPropertyEnum.IsVisibleForCO, FaultFieldTypeEnum.Switch],
    [FieldPropertyEnum.IsVisibleForCT, FaultFieldTypeEnum.Switch],
    [FieldPropertyEnum.Opt1, FaultFieldTypeEnum.Switch],
    [FieldPropertyEnum.Opt2, FaultFieldTypeEnum.Switch],
    [FieldPropertyEnum.Opt3, FaultFieldTypeEnum.Switch],
    [FieldPropertyEnum.Opt4, FaultFieldTypeEnum.Switch],
    [FieldPropertyEnum.Opt5, FaultFieldTypeEnum.Switch],
    [FieldPropertyEnum.Opt6, FaultFieldTypeEnum.Switch],
    [FieldPropertyEnum.Opt7, FaultFieldTypeEnum.Switch],
    [FieldPropertyEnum.Opt8, FaultFieldTypeEnum.Switch],
    [FieldPropertyEnum.Opt9, FaultFieldTypeEnum.Switch],
    [FieldPropertyEnum.Costs1, FaultFieldTypeEnum.Number],
    [FieldPropertyEnum.Costs2, FaultFieldTypeEnum.Number],
    [FieldPropertyEnum.Costs3, FaultFieldTypeEnum.Number],
    [FieldPropertyEnum.Costs4, FaultFieldTypeEnum.Number],
    [FieldPropertyEnum.Costs5, FaultFieldTypeEnum.Number],
  ]);

  permissionEditFaults$ = this.permissionService.hasProjectActionPermissions([ProjectActionPermissionEnum.EditFault]);
  permissionEditCtNumber$ = this.permissionService.hasProjectActionPermissions([ProjectActionPermissionEnum.EditCtNumber]);
  permissionEditAssignedStructureOfFault$ = this.permissionService.hasProjectActionPermissions([ProjectActionPermissionEnum.EditAssignedStructureOfFault]);

  constructor(public readonly permissionService: MmProjectPermissionsService) { }

  getFaultFieldGroups(fields: FieldMinimal[]): FaultFieldGroups {
    const faultFieldsGroups: FaultFieldGroups = {};
    faultFieldsGroups.descriptionFields = [];
    faultFieldsGroups.executionFields = [];
    faultFieldsGroups.responsibleFields = [];
    faultFieldsGroups.classificationFields = [];
    faultFieldsGroups.projectFields = [];
    faultFieldsGroups.costFields = [];
    fields.forEach(field => {
      this.addFieldToGroup(faultFieldsGroups, this.createFaultField(field));
    });

    faultFieldsGroups.descriptionFields = faultFieldsGroups.descriptionFields.filter(f => !!f);
    faultFieldsGroups.executionFields = faultFieldsGroups.executionFields.filter(f => !!f);
    faultFieldsGroups.responsibleFields = faultFieldsGroups.responsibleFields.filter(f => !!f);
    faultFieldsGroups.classificationFields = faultFieldsGroups.classificationFields.filter(f => !!f);
    faultFieldsGroups.projectFields = faultFieldsGroups.projectFields.filter(f => !!f);
    faultFieldsGroups.costFields = faultFieldsGroups.costFields.filter(f => !!f);

    return faultFieldsGroups;
  }

  createFaultField(field: FieldMinimal): FaultField {
    const faultField: FaultField = {...field};

    if (field.Id === 'ProjectSpecific1_Name2' ) {
      field.Prop = FieldPropertyEnum.UserDefined1;
    }
    if (field.Id === 'ProjectSpecific2_Name2' ) {
      field.Prop = FieldPropertyEnum.UserDefined2;
    }

    if (field.Id === 'Classification1_Name1') {
      field.Global = field.GroupFields.find(x => x.Id === 'Classification1_Name2').Global;
    }
    if (field.Id === 'Classification2_Name1') {
      field.Global = field.GroupFields.find(x => x.Id === 'Classification2_Name2').Global;
    }
    if (field.Id === 'Classification3_Name1') {
      field.Global = field.GroupFields.find(x => x.Id === 'Classification3_Name2').Global;
    }
    if (field.Id === 'Classification4_Name1') {
      field.Global = field.GroupFields.find(x => x.Id === 'Classification4_Name2').Global;
    }

    faultField.Name = field.Project || field.Global;
    faultField.Type = this.propertyToControlTypeMap.get(field.Prop);
    return faultField;
  }

  private addFieldToGroup(faultFieldsGroups: FaultFieldGroups,  field: FaultField) {
    let index = descriptionFieldProperties.indexOf(field.Prop);
    if (index > -1) {
      faultFieldsGroups.descriptionFields[index] = field;
      return;
    }
    index = executionFieldProperties.indexOf(field.Prop);
    if (index > -1) {
      faultFieldsGroups.executionFields[index] = field;
      return;
    }
    index = responsibleFieldProperties.indexOf(field.Prop);
    if (index > -1) {
      faultFieldsGroups.responsibleFields[index] = field;
      return;
    }
    index = classificationFieldProperties.indexOf(field.Prop);
    if (index > -1) {
      faultFieldsGroups.classificationFields[index] = field;
      return;
    }
    index = projectFieldProperties.indexOf(field.Prop);
    if (index > -1) {
      faultFieldsGroups.projectFields[index] = field;
      return;
    }
    index = costFieldProperties.indexOf(field.Prop);
    if (index > -1) {
      faultFieldsGroups.costFields[index] = field;
      return;
    }
  }


}
